// Small SVG charts for analytics (no chart library): a line chart with a
// comparison series and a hover crosshair, and horizontal bar lists. Thin
// marks, recessive grid, one axis, text in ink tokens (dataviz rules).
import { useMemo, useRef, useState } from "react";
import { cx } from "./ui";

export type LinePoint = { label: string; value: number; compare?: number };

const W = 640, H = 220, PAD = { top: 12, right: 12, bottom: 24, left: 48 };

function niceMax(v: number) {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

// axisFormat: shorter numbers for the y-axis (e.g. "1.2K"); format is used
// in the tooltip.
export function LineChart({ points, format, axisFormat, label, compareLabel, height = H }: {
  points: LinePoint[]; format: (n: number) => string; axisFormat?: (n: number) => string; label: string; compareLabel?: string; height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const ref = useRef<SVGSVGElement>(null);
  const hasCompare = points.some((p) => p.compare !== undefined);
  const max = useMemo(() => niceMax(Math.max(0, ...points.map((p) => Math.max(p.value, p.compare ?? 0)))), [points]);
  const ih = height - PAD.top - PAD.bottom, iw = W - PAD.left - PAD.right;
  const x = (i: number) => PAD.left + (points.length <= 1 ? iw / 2 : (i * iw) / (points.length - 1));
  const y = (v: number) => PAD.top + ih - (v / max) * ih;
  const path = (get: (p: LinePoint) => number | undefined) =>
    points.map((p, i) => { const v = get(p); return v === undefined ? "" : `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`; }).join(" ");
  const ticks = [0, 0.5, 1].map((f) => f * max);
  const step = Math.max(1, Math.ceil(points.length / 6));
  const onMove = (e: React.PointerEvent) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r || !points.length) return;
    const px = ((e.clientX - r.left) / r.width) * W;
    const i = Math.round(((px - PAD.left) / iw) * (points.length - 1));
    setHover(Math.min(points.length - 1, Math.max(0, i)));
  };
  const h = hover !== null ? points[hover] : null;
  return (
    <div className="relative">
      {hasCompare && (
        <div className="mb-2 flex gap-4 text-xs text-ink-muted">
          <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 rounded bg-brand" />{label}</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-4 rounded border-t-2 border-dashed border-ink-subtle" />{compareLabel}</span>
        </div>
      )}
      <svg ref={ref} viewBox={`0 0 ${W} ${height}`} className="w-full touch-none" role="img" aria-label={label}
        onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} className="stroke-line" strokeWidth={1} />
            <text x={PAD.left - 6} y={y(t) + 4} textAnchor="end" className="fill-ink-subtle text-[10px]">{(axisFormat ?? format)(t)}</text>
          </g>
        ))}
        {points.map((p, i) => (i % step === 0 || i === points.length - 1) && (
          // The edge labels anchor inward so they're never clipped.
          <text key={i} x={x(i)} y={height - 6} textAnchor={points.length > 1 && i === 0 ? "start" : points.length > 1 && i === points.length - 1 ? "end" : "middle"} className="fill-ink-subtle text-[10px]">{p.label}</text>
        ))}
        {hasCompare && <path d={path((p) => p.compare)} fill="none" strokeWidth={2} strokeDasharray="4 4" className="stroke-ink-subtle" />}
        <path d={path((p) => p.value)} fill="none" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" className="stroke-brand" />
        {h && hover !== null && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + ih} className="stroke-line-strong" strokeWidth={1} />
            <circle cx={x(hover)} cy={y(h.value)} r={4} className="fill-brand stroke-white" strokeWidth={2} />
          </g>
        )}
      </svg>
      {h && hover !== null && (
        <div className="pointer-events-none absolute top-6 z-10 rounded-md border border-line bg-white px-2.5 py-1.5 text-xs shadow-sm"
          style={{ insetInlineStart: `min(calc(${(x(hover) / W) * 100}% + 8px), calc(100% - 160px))` }}>
          <div className="font-medium text-ink">{h.label}</div>
          <div className="text-ink-muted">{label}: <span className="text-ink">{format(h.value)}</span></div>
          {h.compare !== undefined && <div className="text-ink-muted">{compareLabel}: <span className="text-ink">{format(h.compare)}</span></div>}
        </div>
      )}
    </div>
  );
}

// BarList: ranked rows with a proportional bar behind the value.
export function BarList({ rows, format, empty }: { rows: { key: string; label: string; value: number; hint?: string }[]; format: (n: number) => string; empty: string }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <p className="py-4 text-center text-sm text-ink-muted">{empty}</p>;
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => (
        <li key={r.key} className="group relative flex items-center justify-between gap-3 rounded px-2 py-1.5 text-sm" title={r.hint}>
          <span className="absolute inset-y-0 start-0 rounded bg-brand-tint" style={{ width: `${(r.value / max) * 100}%` }} aria-hidden />
          <span className="relative min-w-0 truncate">{r.label || "—"}</span>
          <span className="relative shrink-0 font-medium tabular-nums">{format(r.value)}</span>
        </li>
      ))}
    </ul>
  );
}

// Funnel: steps with the share kept from the first.
export function Funnel({ steps, num }: { steps: { label: string; count: number }[]; num: (n: number) => string }) {
  const first = Math.max(1, steps[0]?.count ?? 1);
  return (
    <ol className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {steps.map((s, i) => (
        <li key={s.label} className="rounded-lg border border-line p-3">
          <div className="text-xs text-ink-muted">{s.label}</div>
          <div className="mt-1 text-lg font-semibold tabular-nums">{num(s.count)}</div>
          <div className="mt-2 h-1.5 overflow-hidden rounded bg-muted"><div className={cx("h-full rounded bg-brand")} style={{ width: `${(s.count / first) * 100}%` }} /></div>
          {i > 0 && <div className="mt-1 text-[11px] text-ink-subtle">{((s.count / first) * 100).toFixed(1)}%</div>}
        </li>
      ))}
    </ol>
  );
}

// Sparkline: a tiny trend line for a KPI tile (no axes, no labels; the
// tile shows the number). Area fill in the brand tint.
export function Sparkline({ values, className, label }: { values: number[]; className?: string; label?: string }) {
  const w = 120, h = 36, pad = 2;
  if (values.length < 2) return <div className={cx("h-9", className)} aria-hidden />;
  const max = Math.max(...values), min = Math.min(...values, 0);
  const span = max - min || 1;
  const pts = values.map((v, i) => [pad + (i * (w - 2 * pad)) / (values.length - 1), h - pad - ((v - min) / span) * (h - 2 * pad)] as const);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${pts[pts.length - 1]![0].toFixed(1)},${h} L${pts[0]![0].toFixed(1)},${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className={cx("h-9 w-full rtl:-scale-x-100", className)} role={label ? "img" : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <path d={area} className="fill-brand-tint" />
      <path d={line} fill="none" strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" className="stroke-brand" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
